<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="bg-grey-lighten-4 elevation-0">
          <v-toolbar color="primary" density="comfortable">
            <v-toolbar-title class="text-white">
              {{ title }}
            </v-toolbar-title>
            <v-spacer />
            <div class="d-flex ga-2">
              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="cancel">
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                <span>Fermer</span>
              </v-tooltip>

              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="save">
                    <v-icon>mdi-content-save-all</v-icon>
                  </v-btn>
                </template>
                <span>Sauvegarder</span>
              </v-tooltip>
            </div>
          </v-toolbar>

          <v-card-text v-if="!loading">
            <v-container fluid>
              <v-row>
                <!-- Avatar -->
                <v-col md="3" cols="12">
                  <v-img v-if="user" max-width="10em" :src="selectedAvatarSrc" class="avatar" />
                  <div class="text-subtitle-2 mt-4 mb-2">Choisir un avatar</div>
                  <v-item-group v-model="selectedAvatarName" selected-class="avatar-option--selected">
                    <div class="avatar-options">
                      <v-item v-for="avatar in avatarOptions" :key="avatar.name" :value="avatar.name"
                        v-slot="{ selectedClass, toggle }">
                        <v-avatar
                          :class="['avatar-option', selectedClass]"
                          size="52"
                          :image="avatar.src"
                          @click="toggle"
                        />
                      </v-item>
                    </div>
                  </v-item-group>
                </v-col>

                <!-- Form Fields -->
                <v-col md="9" cols="12">
                  <v-row>
                    <v-col cols="12">
                      <v-checkbox v-model="user.technical" label="Technicien" />
                    </v-col>

                    <v-col md="4" cols="12">
                      <v-text-field v-model="user.firstname" maxlength="15" :rules="rules.prenom" label="Prénom *" />
                    </v-col>

                    <v-col md="4" cols="12">
                      <v-text-field v-model="user.lastname" maxlength="15" :rules="rules.nom" label="Nom *" />
                    </v-col>

                    <v-col md="4" cols="12">
                      <v-text-field v-model="user.username" maxlength="15" label="Nom d'utilisateur *"
                        autocomplete="off" />
                    </v-col>

                    <v-col md="4" cols="12">
                      <v-text-field v-model="user.password" type="password" label="Mot de passe *"
                        autocomplete="new-password" />
                    </v-col>

                    <v-col md="4" cols="12">
                      <v-text-field v-model="user.email" type="email" :rules="rules.email" label="Courriel"
                        autocomplete="off" />
                    </v-col>

                    <v-col md="4" cols="12">
                      <v-text-field v-model="user.telephone" :rules="rules.phoneNb" label="Téléphone"
                        autocomplete="off" />
                    </v-col>

                    <!-- Roles Table -->
                    <v-col cols="12">
                      <v-data-table :headers="roleHeaders" :items="userRoles" class="roles-table" hide-default-footer
                        dense>
                        <template #item.assigned="{ item }">
                          <div class="checkbox-cell">
                            <v-checkbox v-model="item.selected" :disabled="item.value !== 'ROLE_ADMIN' && isDisabled"
                              @change="selectRole(item)" hide-details dense color="primary" />
                          </div>
                        </template>
                      </v-data-table>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useRoute, useRouter, onBeforeRouteUpdate } from "vue-router";
import { useAppStore } from "@/store/app";
import { isValidEmail, isValidName, isValidPhoneNumber } from "@/utils/validation-util";
import { TableColumn, User } from "@/types";
import { getUserById, postUser, putUser } from "@/utils/user-api";
import avatar1 from "@/assets/img/avatar1.png";
import avatar2 from "@/assets/img/avatar2.png";
import avatar3 from "@/assets/img/avatar3.png";
import avatar4 from "@/assets/img/avatar4.png";
import avatar5 from "@/assets/img/avatar5.png";

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();

const isDisabled = ref(false);
const loading = ref(false);
const title = ref("");

const user = ref<User>({ roles: [] } as User);

const avatarOptions = [
  { name: "avatar1.png", src: avatar1 },
  { name: "avatar2.png", src: avatar2 },
  { name: "avatar3.png", src: avatar3 },
  { name: "avatar4.png", src: avatar4 },
  { name: "avatar5.png", src: avatar5 },
];

const avatarMap = computed(() =>
  avatarOptions.reduce((acc, avatar) => {
    acc[avatar.name] = avatar.src;
    return acc;
  }, {} as Record<string, string>)
);

const selectedAvatarName = computed<string>({
  get: () => normalizeAvatarName(user.value.avatar || ""),
  set: (value: string) => {
    user.value.avatar = value;
  },
});

const selectedAvatarSrc = computed(() => {
  const normalized = selectedAvatarName.value;
  if (normalized && avatarMap.value[normalized]) {
    return avatarMap.value[normalized];
  }

  const fallback = user.value.avatar || "";
  return fallback || "";
});

const roleHeaders: TableColumn[] = [
  { title: "Role", key: "label" },
  { title: "Description", key: "description" },
  { title: "Assigned", key: "assigned", align: "center" },
];

const userRoles = ref([
  {
    label: "Admin",
    description: "Accès complet à toutes les fonctionnalités",
    value: "ROLE_ADMIN",
    selected: false,
  },
  {
    label: "Sièges",
    description: "Gestion des sièges",
    value: "ROLE_ACCOUNT",
    selected: false,
  },
  {
    label: "Laveries",
    description: "Gestion des laveries",
    value: "ROLE_LAVERIE",
    selected: false,
  },
  {
    label: "Produits",
    description: "Gestion des produits et pièces détachées",
    value: "ROLE_PRODUCT",
    selected: false,
  },
  {
    label: "Utilisateurs",
    description: "Gestions des utilisateurs",
    value: "ROLE_USER",
    selected: false,
  },
  {
    label: "Editions",
    description: "Editions des documents",
    value: "ROLE_EDITION",
    selected: false,
  },
  {
    label: "Tournées",
    description: "Gestion des tournées et activités",
    value: "ROLE_TOURNEE",
    selected: false,
  },
  {
    label: "Véhicules",
    description: "Gestion des véhicules",
    value: "ROLE_VEHICULE",
    selected: false,
  },
  {
    label: "Pièces détachées",
    description: "Gestion des pièces détachées",
    value: "ROLE_SPARPART",
    selected: false,
  },
  {
    label: "Technicien",
    description: "Accès au tableau de bord technicien",
    value: "ROLE_TECHNICIAN",
    selected: false,
  },
]);

const rules = {
  phoneNb: [() => isValidPhoneNumber(user.value.telephone)],
  email: [() => isValidEmail(user.value.email)],
  prenom: [() => isValidName(user.value.name, "Le prénom n'est pas valide")],
  nom: [() => isValidName(user.value.lastname, "Le nom n'est pas valide")],
};

function selectRole(role: any) {
  if (!user.value.roles) user.value.roles = [];

  if (role.selected) {
    if (role.value === "ROLE_ADMIN") {
      // Admin selected → disable all others
      user.value.roles = ["ROLE_ADMIN"];
      userRoles.value.forEach((r) => {
        if (r.value !== "ROLE_ADMIN") r.selected = false;
      });
    } else {
      // Regular role selected
      if (!user.value.roles.includes(role.value)) {
        user.value.roles.push(role.value);
      }
    }
  } else {
    // Deselect role
    const index = user.value.roles.indexOf(role.value);
    if (index !== -1) user.value.roles.splice(index, 1);
  }

  // Disable other roles if admin is selected
  isDisabled.value = user.value.roles.includes("ROLE_ADMIN");
}

async function save() {
  try {
    loading.value = true;
    const payload = { ...user.value };

    if (!payload.id) {
      user.value = await postUser(payload);
      appStore.sendSuccessNotice("New user has been added.");
      router.replace({ name: "user", params: { id: user.value.id } });
      title.value = "Editer l'utilisateur";
    } else {
      await putUser(user.value.id, user.value);
      appStore.sendSuccessNotice("User has been updated.");
    }
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

function cancel() {
  router.push({ name: "users" });
  resetData();
}

function resetData() {
  isDisabled.value = false;
  userRoles.value.forEach((r) => (r.selected = false));
}

function isCreatingNewUser(routeToCheck = route) {
  return routeToCheck.name === "newuser" || routeToCheck.params.id === "newuser";
}

function normalizeAvatarName(value: string): string {
  if (!value) return "";

  const normalized = value.split("/").pop() ?? value;
  return avatarMap.value[normalized] ? normalized : "";
}

async function initializeUser(routeToCheck = route) {
  loading.value = true;
  try {
    if (isCreatingNewUser(routeToCheck)) {
      title.value = "Nouveau utilisateur";
      user.value = {
        email: '',
        username: '',
        password: '',
        roles: [],
        avatar: avatarOptions[0]?.name || '',
      } as User;
    } else {
      title.value = "Editer l'utilisateur";
      const userId = routeToCheck.params.id as string;
      user.value = await getUserById(userId);
      const normalizedAvatar = normalizeAvatarName(user.value.avatar || "");
      if (normalizedAvatar) {
        user.value.avatar = normalizedAvatar;
      }
    }

    // Sync role selection
    userRoles.value.forEach((r) => {
      r.selected = user.value.roles?.includes(r.value) ?? false;
    });

    isDisabled.value = user.value.roles?.includes("ROLE_ADMIN") ?? false;
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  await initializeUser();
});

onBeforeRouteUpdate(async (to, from) => {
  await initializeUser(to);
});
</script>

<style scoped>
.avatar {
  border-radius: 50%;
}

.avatar-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.avatar-option {
  border: 2px solid transparent;
  cursor: pointer;
}

.avatar-option--selected {
  border-color: rgb(var(--v-theme-primary));
}

.roles-table {
  border-collapse: collapse;
  width: 100%;
}

.roles-table th {
  border: 1px solid #ccc;
  padding: 6px 8px;
}

.roles-table td {
  border: 1px solid #ccc;
  padding: 8px;
  text-align: left;
}

.roles-table tbody tr {
  height: 40px;
}

.checkbox-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
</style>
